import React, { useState, useEffect } from 'react';
import api from './api';
import Sidebar from './Sidebar';
import LeadsView from './views/LeadsView';
import BusinessesView from './views/BusinessesView';
import PlaceCardsView from './views/PlaceCardsView';
import PlaceWorkbenchView from './views/PlaceWorkbenchView';
import LaunchTriageView from './views/LaunchTriageView';
import ComposerPreviewView from './views/ComposerPreviewView';
import GuestDaySlideFixtureView from './views/GuestDaySlideFixtureView';
import ChangeRequestsView from './views/ChangeRequestsView';
import SubscribersView from './views/SubscribersView';
import TaxonomyView from './views/TaxonomyView';
import AgentWorkbenchView from './views/AgentWorkbenchView';

const VIEWS = {
  leads: LeadsView,
  businesses: BusinessesView,
  places: PlaceWorkbenchView,
  launch_triage: LaunchTriageView,
  composer: ComposerPreviewView,
  guest_day_slide: GuestDaySlideFixtureView,
  legacy_places: PlaceCardsView,
  changes: ChangeRequestsView,
  agents: AgentWorkbenchView,
  subscribers: SubscribersView,
  taxonomy: TaxonomyView,
};

const LOCAL_UNAUTHED_FIXTURE_TABS = new Set(['guest_day_slide']);

function isLocalFixturePreview(tab) {
  return typeof window !== 'undefined'
    && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    && LOCAL_UNAUTHED_FIXTURE_TABS.has(tab);
}

function isAuthFailure(error) {
  return error?.status === 401 || error?.status === 403;
}

function parseHashRoute() {
  const raw = window.location.hash.slice(1);
  const [tabRaw, queryRaw = ''] = raw.split('?');
  const tab = VIEWS[tabRaw] ? tabRaw : 'leads';
  const params = new URLSearchParams(queryRaw);
  return {
    tab,
    placeId: params.get('place_id') || params.get('placeMasterId') || '',
    cardKey: params.get('card_key') || params.get('cardKey') || 'primary',
  };
}

function LoginPage({ onLogin }) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      api.setPassword(pw);
      await api.checkAuth();
      onLogin();
    } catch (error) {
      if (isAuthFailure(error)) {
        api.clearPassword();
        setError('Invalid password');
      } else {
        setError('Could not reach the admin API. Try again in a moment.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-lg p-8 w-80 space-y-4">
        <h1 className="text-xl font-semibold text-gray-900">Pocket Host Admin</h1>
        <input
          type="password"
          placeholder="Password"
          value={pw}
          onChange={e => setPw(e.target.value)}
          autoFocus
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={loading || !pw}
          className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
        >
          {loading ? 'Checking…' : 'Sign In'}
        </button>
      </form>
    </div>
  );
}

export default function App() {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem('mph_admin_pw'));
  const [activeTab, setActiveTab] = useState(() => {
    return parseHashRoute().tab;
  });
  const [counts, setCounts] = useState({ change_requests: 0 });
  const [changeRequestFilter, setChangeRequestFilter] = useState(null);
  const [workbenchTarget, setWorkbenchTarget] = useState(() => {
    const route = parseHashRoute();
    return route.tab === 'places' && route.placeId
      ? { placeId: route.placeId, cardKey: route.cardKey || 'primary', openedAt: Date.now() }
      : null;
  });
  const [workbenchReturnToChanges, setWorkbenchReturnToChanges] = useState(null);

  const loadCounts = () => {
    if (!authed) return;
    api.getChangeRequestCounts()
      .then(data => setCounts({ change_requests: data.pending || 0 }))
      .catch(() => {});
  };

  // Verify saved password on mount
  useEffect(() => {
    if (authed) {
      api.checkAuth().catch((error) => {
        if (isAuthFailure(error)) {
          api.clearPassword();
          setAuthed(false);
        }
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadCounts(); }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps

  const canShowLocalFixture = !authed && isLocalFixturePreview(activeTab);
  const View = VIEWS[activeTab];

  if (!authed && !canShowLocalFixture) {
    return <LoginPage onLogin={() => setAuthed(true)} />;
  }

  if (canShowLocalFixture) {
    return (
      <div className="min-h-screen overflow-auto bg-gray-50 p-6">
        <View />
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar
        activeTab={activeTab}
        setActiveTab={(tab) => {
          window.location.hash = tab;
          setChangeRequestFilter(null);
          setWorkbenchTarget(null);
          setWorkbenchReturnToChanges(null);
          setActiveTab(tab);
        }}
        counts={counts}
        onLogout={() => { api.clearPassword(); setAuthed(false); }}
      />
      <main className="flex-1 overflow-auto p-6">
        <View
          onChangeRequestsChanged={loadCounts}
          onGoToChanges={(placeId, cardKey = null) => {
            window.location.hash = 'changes';
            setChangeRequestFilter(placeId ? { placeId, cardKey: cardKey || null } : null);
            setWorkbenchReturnToChanges(null);
            setActiveTab('changes');
          }}
          onOpenPlace={(placeId, cardKey = 'primary', context = null) => {
            window.location.hash = 'places';
            setWorkbenchTarget({ placeId, cardKey, openedAt: Date.now() });
            const hasReturnPlace = context && Object.prototype.hasOwnProperty.call(context, 'placeId');
            const hasReturnCard = context && Object.prototype.hasOwnProperty.call(context, 'cardKey');
            setWorkbenchReturnToChanges(context?.source === 'changes' ? {
              placeId: hasReturnPlace ? context.placeId : placeId,
              cardKey: hasReturnCard ? context.cardKey : (cardKey || null),
            } : null);
            setActiveTab('places');
          }}
          onReturnToChanges={() => {
            window.location.hash = 'changes';
            setChangeRequestFilter(workbenchReturnToChanges?.placeId ? {
              placeId: workbenchReturnToChanges.placeId,
              cardKey: workbenchReturnToChanges.cardKey || null,
            } : null);
            setWorkbenchTarget(null);
            setWorkbenchReturnToChanges(null);
            setActiveTab('changes');
          }}
          returnToChanges={activeTab === 'places' ? workbenchReturnToChanges : null}
          filterPlaceId={activeTab === 'changes' ? changeRequestFilter?.placeId : undefined}
          filterCardKey={activeTab === 'changes' ? changeRequestFilter?.cardKey : undefined}
          openTarget={activeTab === 'places' ? workbenchTarget : undefined}
        />
      </main>
    </div>
  );
}
